/**
 * Emergency coordination: the on-duty switch (M36-T04) and working requests
 * (M36-T06), ADR-0065. Held by `support` and `amtc_admin`; the server's
 * `emergency_coordination:*` capabilities are the real guard.
 *
 * Shapes follow the backend's `schemas/coordination.py` response models, not
 * what a stub would find convenient (the M35-T06 lesson: a stub that invents
 * the shape lets every test pass while the screen loads nothing).
 */
import { apiFetch } from './client'

// ------------------------------------------------------------------ the switch

export const ON_DUTY_KEY = 'emergency_coordination_on_duty'
export const SWITCH_QUERY_KEY = [
  'admin',
  'operations-switch',
  ON_DUTY_KEY,
] as const

export type OperationsSwitch = {
  key: string
  is_on: boolean
  on_until: string | null
  changed_by_name: string | null
  changed_at: string | null
  /** On, and `on_until` still ahead. An expired switch reads false. */
  available: boolean
  /** The furthest ahead the switch may be set. Never hard-coded in the page. */
  max_on_hours: number
}

export type SwitchUpdate = { on: true; until: string } | { on: false }

export async function fetchOnDutySwitch(
  token: string | null,
): Promise<OperationsSwitch> {
  const body = await apiFetch<{ data: OperationsSwitch }>(
    `/api/v1/admin/operations-switches/${ON_DUTY_KEY}`,
    { token },
  )
  return body.data
}

export async function setOnDutySwitch(
  token: string | null,
  update: SwitchUpdate,
): Promise<OperationsSwitch> {
  const body = await apiFetch<{ data: OperationsSwitch }>(
    `/api/v1/admin/operations-switches/${ON_DUTY_KEY}`,
    { token, method: 'PUT', body: update },
  )
  return body.data
}

// ------------------------------------------------------------------ requests

export const COORDINATION_STATUSES = [
  'request_received',
  'acknowledged',
  'contact_attempted',
  'hospital_contacted',
  'awaiting_response',
  'hospital_confirmed',
  'unable_to_coordinate',
  'closed',
] as const

export type CoordinationStatus = (typeof COORDINATION_STATUSES)[number]

/** The spec's labels (§D.2), not the stored keys. */
export const STATUS_LABELS: Record<CoordinationStatus, string> = {
  request_received: 'Request received',
  acknowledged: 'Acknowledged',
  contact_attempted: 'Contact attempted',
  hospital_contacted: 'Hospital contacted',
  awaiting_response: 'Awaiting response',
  hospital_confirmed: 'Hospital confirmed',
  unable_to_coordinate: 'Unable to coordinate',
  closed: 'Closed',
}

export function statusLabel(status: string): string {
  return (
    STATUS_LABELS[status as CoordinationStatus] ?? status.replace(/_/g, ' ')
  )
}

export type HospitalResponse = 'no_answer' | 'cannot_accept' | 'accepted'

export const QUEUE_KEY = ['admin', 'emergency-coordination'] as const

export type QueueItem = {
  id: string
  reference: string
  created_at: string
  callback_phone_masked: string | null
  hospital_name: string | null
  status: string
  /** Null means unacknowledged (ADR-0065 decision 4). */
  acknowledged_at: string | null
  feedback_consent: boolean
  purged: boolean
}

export type Queue = {
  data: QueueItem[]
  total: number
  /** Every unacknowledged request, whatever the filters and the cap. */
  unacknowledged_total: number
  ack_warning_minutes: number
  max_contact_attempts: number
}

export type QueueFilters = {
  status: string | null
  open: boolean
  feedback: boolean
  dateFrom: string
  dateTo: string
}

export async function fetchQueue(
  token: string | null,
  f: QueueFilters,
): Promise<Queue> {
  const params = new URLSearchParams({ limit: '100' })
  if (f.status) params.set('status', f.status)
  if (f.open) params.set('open', 'true')
  if (f.feedback) params.set('feedback_consent', 'true')
  if (f.dateFrom) params.set('date_from', f.dateFrom)
  if (f.dateTo) params.set('date_to', f.dateTo)
  return apiFetch<Queue>(`/api/v1/admin/emergency-coordination?${params}`, {
    token,
  })
}

export type ChecklistStep = {
  step: number
  done_at: string | null
  done_by_name: string | null
}
export type CoordinationNote = {
  id: string
  author_name: string | null
  body: string | null
  created_at: string
}
export type HistoryEntry = {
  action: string
  actor_name: string | null
  actor_role: string
  before: Record<string, unknown>
  after: Record<string, unknown>
  occurred_at: string
}

export type CoordinationDetail = {
  id: string
  reference: string
  status: string
  created_at: string
  updated_at: string
  purged: boolean
  purged_note: string | null
  callback_phone: string | null
  location_text: string | null
  lat: number | null
  lng: number | null
  description: string | null
  hospital: {
    slug: string
    name: string
    verified_emergency_phone: string | null
    emergency_info_verified: boolean
  } | null
  consent_given_at: string
  feedback_consent_at: string | null
  checklist: ChecklistStep[]
  contact_attempts: number
  hospital_number_dialled: string | null
  hospital_response: HospitalResponse | null
  hospital_rep_name: string | null
  hospital_rep_role: string | null
  hospital_confirmed_at: string | null
  unable_reason: string | null
  outcome_note: string | null
  closed_at: string | null
  notes: CoordinationNote[]
  history: HistoryEntry[]
  ack_warning_minutes: number
  max_contact_attempts: number
}

export type CoordinationUpdate = {
  status?: CoordinationStatus
  acknowledge?: true
  step2_done?: boolean
  step3_done?: boolean
  step4_done?: boolean
  step5_done?: boolean
  step6_done?: boolean
  hospital_response?: HospitalResponse
  hospital_number_dialled?: string
  hospital_rep_name?: string
  hospital_rep_role?: string
  hospital_confirmed_at?: string
  unable_reason?: string
  outcome_note?: string
  note?: string
}

export const detailKey = (id: string) => [...QUEUE_KEY, 'detail', id] as const

export async function fetchRequest(
  token: string | null,
  id: string,
): Promise<CoordinationDetail> {
  const body = await apiFetch<{ data: CoordinationDetail }>(
    `/api/v1/admin/emergency-coordination/${encodeURIComponent(id)}`,
    { token },
  )
  return body.data
}

export async function updateRequest(
  token: string | null,
  id: string,
  update: CoordinationUpdate,
): Promise<CoordinationDetail> {
  const body = await apiFetch<{ data: CoordinationDetail }>(
    `/api/v1/admin/emergency-coordination/${encodeURIComponent(id)}`,
    { token, method: 'PATCH', body: update },
  )
  return body.data
}

export async function addNote(
  token: string | null,
  id: string,
  text: string,
): Promise<void> {
  await apiFetch<{ data: CoordinationNote }>(
    `/api/v1/admin/emergency-coordination/${encodeURIComponent(id)}/notes`,
    { token, method: 'POST', body: { body: text } },
  )
}
